import { useState } from 'react';
import {
  usePendingPosts,
  useApprovePost,
  useRejectPost,
} from '@/lib/hooks/usePosts';
import { useToastNotification } from '@/lib/hooks/useToastNotification';
import { Post } from '@/components/community/Post';
import { PostListSkeleton } from '@/components/community/PostListSkeleton';
import { Button } from '@/components/ui/button';
import { ActionDrawer } from '@/components/ui/action-drawer';
import { Textarea } from '@/components/ui/textarea';
import { ToastNotification } from '@/components/ui/ToastNotification';
import { formatDateTimeForList } from '@/lib/utils/date.utils';
import type { PostWithAuthor } from '@/lib/types/database';
import { CheckCircleIcon, XCircleIcon } from '@phosphor-icons/react';

export function ModeratePosts() {
  const { data: pendingPosts, isLoading, error } = usePendingPosts();
  const approvePost = useApprovePost();
  const rejectPost = useRejectPost();
  const { toast, showToast, hideToast } = useToastNotification();
  const [selectedPost, setSelectedPost] = useState<PostWithAuthor | null>(null);
  const [isApproveDialogOpen, setIsApproveDialogOpen] = useState(false);
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  if (isLoading) {
    return (
      <div className="flex flex-col w-full max-w-4xl mx-auto px-4 pb-4 mt-[34px]">
        <h1 className="mb-6">Pending Posts</h1>
        <PostListSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center flex-col w-full max-w-4xl mx-auto px-4 pb-4 mt-[34px]">
        <h1 className="mb-6">Pending Posts</h1>
        <p className="text-body text-red-400">
          Failed to load pending posts. Please try again later.
        </p>
      </div>
    );
  }

  if (!pendingPosts || pendingPosts.length === 0) {
    return (
      <div className="flex justify-center items-center flex-col w-full max-w-4xl mx-auto px-4 pb-4 mt-[34px]">
        <h1 className="text-h2 mb-6">Pending Posts</h1>
        <p className="text-body">No posts pending approval. All clear!</p>
      </div>
    );
  }

  const handleApproveClick = (post: PostWithAuthor) => {
    setSelectedPost(post);
    setIsApproveDialogOpen(true);
  };

  const handleConfirmApprove = async () => {
    if (!selectedPost) {
      return;
    }

    try {
      await approvePost.mutateAsync(selectedPost.id);
      setIsApproveDialogOpen(false);
      setSelectedPost(null);
      showToast({
        title: 'Post approved',
        description: 'The post is now visible in the community feed.',
        variant: 'success',
      });
    } catch (error) {
      showToast({
        title: 'Approval failed',
        description:
          error instanceof Error
            ? error.message
            : 'Failed to approve post. Please try again.',
        variant: 'error',
      });
      console.error('Failed to approve post:', error);
    }
  };

  const handleCloseApproveDialog = (open: boolean) => {
    setIsApproveDialogOpen(open);
    if (!open) {
      setSelectedPost(null);
    }
  };

  const handleRejectClick = (post: PostWithAuthor) => {
    setSelectedPost(post);
    setRejectionReason('');
    setIsRejectDialogOpen(true);
  };

  const handleConfirmReject = async () => {
    if (!selectedPost) {
      return;
    }

    try {
      await rejectPost.mutateAsync({
        postId: selectedPost.id,
        reason: rejectionReason.trim() || undefined,
      });
      setIsRejectDialogOpen(false);
      setSelectedPost(null);
      setRejectionReason('');
      showToast({
        title: 'Post rejected',
        description:
          'The post has been rejected and the author has been notified.',
        variant: 'success',
      });
    } catch (error) {
      showToast({
        title: 'Rejection failed',
        description:
          error instanceof Error
            ? error.message
            : 'Failed to reject post. Please try again.',
        variant: 'error',
      });
      console.error('Failed to reject post:', error);
    }
  };

  const handleCloseRejectDialog = (open: boolean) => {
    setIsRejectDialogOpen(open);
    if (!open) {
      setSelectedPost(null);
      setRejectionReason('');
    }
  };

  const isProcessing = approvePost.isPending || rejectPost.isPending;

  return (
    <>
      <div className="flex flex-col w-full max-w-4xl mx-auto px-4 pb-4 mt-[34px]">
        <h1 className="mb-6">Pending Posts</h1>

        <div className="divide-y divide-slate-600">
          {pendingPosts.map((post) => {
            const authorName = post.author_name || 'Unknown User';
            const formattedTimestamp = formatDateTimeForList(post.created_at);

            return (
              <div key={post.id} className="py-4 first:pt-0 last:pb-0">
                <Post
                  postId={post.id}
                  author={authorName}
                  disableClick={true}
                  postTimestamp={formattedTimestamp}
                  imgUrl={post.image_url || undefined}
                  content={post.content}
                  commentCount={post.comment_count}
                  isAdminOrOwner={false}
                  showActionBar={false}
                />

                {/* Moderation Actions */}
                <div className="flex gap-3 mt-4">
                  <Button
                    onClick={() => handleApproveClick(post)}
                    disabled={isProcessing}
                    className="flex-1 h-12 bg-green-400 hover:bg-green-700 text-white"
                  >
                    <CheckCircleIcon size={24} weight="bold" />
                    Approve
                  </Button>
                  <Button
                    onClick={() => handleRejectClick(post)}
                    disabled={isProcessing}
                    variant="destructive"
                    className="flex-1 h-12"
                  >
                    <XCircleIcon size={24} weight="bold" />
                    Reject
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Approve Dialog */}
      <ActionDrawer
        open={isApproveDialogOpen}
        onOpenChange={handleCloseApproveDialog}
        title="Approve Post?"
        description="This post will become visible to all users in the community feed. The author will be notified."
        confirmLabel="Approve"
        cancelLabel="Cancel"
        confirmVariant="default"
        onConfirm={handleConfirmApprove}
        isLoading={approvePost.isPending}
        loadingLabel="Approving..."
      />

      {/* Reject Dialog */}
      <ActionDrawer
        open={isRejectDialogOpen}
        onOpenChange={handleCloseRejectDialog}
        title="Reject Post?"
        description="Optionally provide a reason for rejection. The author will be notified."
        confirmLabel="Reject"
        cancelLabel="Cancel"
        confirmVariant="destructive"
        onConfirm={handleConfirmReject}
        isLoading={rejectPost.isPending}
        loadingLabel="Rejecting..."
      >
        <Textarea
          placeholder="Reason for rejection (optional)"
          value={rejectionReason}
          onChange={(e) => setRejectionReason(e.target.value)}
          disabled={rejectPost.isPending}
          className="min-h-[100px] resize-none"
        />
      </ActionDrawer>

      {/* Toast Notification */}
      <ToastNotification
        open={toast.open}
        onClose={hideToast}
        title={toast.title}
        description={toast.description}
        variant={toast.variant}
        position="bottom"
      />
    </>
  );
}
